import React, { useCallback, useEffect, useState } from "react";
import { getQualityActions, updateQualityAction } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { AlertTriangle, CheckCircle2, ListChecks, RotateCcw, Loader2 } from "lucide-react";
import { fmtDate } from "./QualityDetail";
import { log } from "../../lib/log";

/* Open corrective actions (all controls) with overdue flag; majors/masters can close or reopen them. */
const ActionsList = ({ compact = false, onChanged }) => {
  const [data, setData] = useState({ items: [], open: 0, overdue: 0 });
  const [showDone, setShowDone] = useState(false);
  const [busy, setBusy] = useState("");
  const { toast } = useToast();

  const load = useCallback(() => getQualityActions(showDone ? { all: true } : {}).then((r) => setData(r.data)).catch(log.error), [showDone]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (a, done) => {
    setBusy(a.id);
    try { await updateQualityAction(a.control_id, a.id, { done }); toast({ title: done ? "Action clôturée ✓" : "Action réouverte" }); await load(); onChanged?.(); }
    catch (e) { toast({ title: "Erreur", description: e?.response?.data?.detail || "Mise à jour impossible", variant: "destructive" }); }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="quality-actions">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="font-semibold text-neutral-900 flex items-center gap-2 text-sm"><ListChecks size={16} className="text-brand" /> Actions correctives
          <span className="text-xs font-normal text-neutral-500" data-testid="quality-actions-counts">{data.open} ouverte(s){data.overdue > 0 && <span className="text-red-600 font-semibold"> · {data.overdue} en retard</span>}</span></p>
        <label className="text-xs text-neutral-600 flex items-center gap-1.5"><input type="checkbox" className="accent-brand" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} data-testid="quality-actions-show-done" /> Afficher les clôturées</label>
      </div>
      {data.items.length === 0 ? <p className="text-sm text-neutral-500 text-center py-4" data-testid="quality-actions-empty">Aucune action corrective en cours.</p> : (
        <ul className="divide-y divide-neutral-100">
          {data.items.map((a) => {
            const done = a.status === "terminee";
            return (
              <li key={a.id} className={`flex items-start gap-3 py-2.5 ${done ? "opacity-60" : ""}`} data-testid={`quality-action-${a.id}`}>
                <button disabled={busy === a.id} onClick={() => toggle(a, !done)} title={done ? "Réouvrir" : "Clôturer"} data-testid={`quality-action-toggle-${a.id}`}
                  className={`mt-0.5 shrink-0 w-6 h-6 rounded-full border flex items-center justify-center ${done ? "bg-emerald-500 border-emerald-500 text-white" : "border-neutral-300 text-transparent hover:border-brand hover:text-brand"}`}>
                  {busy === a.id ? <Loader2 size={12} className="animate-spin text-neutral-500" /> : done ? <CheckCircle2 size={14} /> : <RotateCcw size={12} />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm text-neutral-900 ${done ? "line-through" : ""}`}>{a.label}</p>
                  <p className="text-xs text-neutral-500">{a.client_nom}{a.ville && ` · ${a.ville}`}{!compact && ` · contrôle du ${fmtDate(a.control_date)} par ${a.major_nom}`}{done && a.done_by && ` · clôturée par ${a.done_by} le ${fmtDate(a.done_at)}`}</p>
                </div>
                {a.echeance && (
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 flex items-center gap-1 ${a.overdue ? "bg-red-100 text-red-700" : "bg-neutral-100 text-neutral-600"}`} data-testid={`quality-action-due-${a.id}`}>
                    {a.overdue && <AlertTriangle size={11} />} {a.overdue ? "En retard · " : ""}{fmtDate(a.echeance)}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ActionsList;
